import React from 'react';
import { Box, Paper, Typography, Grid, Chip, alpha } from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';

export default function OverallHealthSummary({ metrics }) {
  if (!metrics || metrics.length === 0) return null;

  const totalServices = metrics.length;
  const healthyServices = metrics.filter(m => m.status === 'UP').length;
  const degradedServices = metrics.filter(m => m.status === 'DEGRADED').length;
  const downServices = metrics.filter(m => m.status === 'DOWN').length;

  const avgResponseTime = metrics.reduce((acc, m) => {
    return acc + (m.current_response_time || 0);
  }, 0) / metrics.filter(m => m.current_response_time).length || 0;

  const avgUptime = metrics.reduce((acc, m) => {
    return acc + (m.uptime_percentage || 0);
  }, 0) / totalServices || 0;

  const uniqueGroups = [...new Set(metrics.map(m => m.container_group))].filter(Boolean);

  // Determine overall status
  const getOverallStatus = () => {
    if (downServices > 0) return { label: 'Critical', color: '#ef4444', icon: ErrorIcon };
    if (degradedServices > 0) return { label: 'Degraded', color: '#f59e0b', icon: WarningIcon };
    if (healthyServices === totalServices) return { label: 'All Systems Operational', color: '#10b981', icon: HealthyIcon };
    return { label: 'Partial', color: '#6b7280', icon: WarningIcon };
  };

  const status = getOverallStatus();
  const StatusIcon = status.icon;

  const stats = [
    {
      label: 'Healthy',
      value: healthyServices,
      color: '#10b981',
      icon: HealthyIcon,
    },
    {
      label: 'Degraded',
      value: degradedServices,
      color: '#f59e0b',
      icon: WarningIcon,
    },
    {
      label: 'Down',
      value: downServices,
      color: '#ef4444',
      icon: ErrorIcon,
    },
    {
      label: 'Avg Response',
      value: `${avgResponseTime.toFixed(0)}ms`,
      color: '#3b82f6',
      icon: TimerIcon,
    },
    {
      label: 'Avg Uptime',
      value: `${avgUptime.toFixed(1)}%`,
      color: '#8b5cf6',
      icon: SpeedIcon,
    },
    {
      label: 'Service Groups',
      value: uniqueGroups.length,
      color: '#ec4899',
      icon: LayersIcon,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(status.color, 0.05)} 0%, ${alpha(status.color, 0.1)} 100%)`,
        border: `1px solid ${alpha(status.color, 0.2)}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              bgcolor: alpha(status.color, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StatusIcon sx={{ fontSize: '2rem', color: status.color }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              System Health
            </Typography>
            <Chip
              label={status.label}
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: alpha(status.color, 0.15),
                color: status.color,
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          </Box>
        </Box>
        
        <Typography variant="h3" sx={{ fontWeight: 800, color: status.color }}>
          {healthyServices}/{totalServices}
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2}>
        {stats.map((stat) => {
          const StatIcon = stat.icon;
          return (
            <Grid item xs={6} sm={4} md={2} key={stat.label}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  textAlign: 'center',
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                }}
              >
                <StatIcon sx={{ fontSize: '1.25rem', color: stat.color, mb: 0.5 }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: stat.color, fontSize: '1.1rem' }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}
